import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useTheme } from '../../../../core/ui/ThemeContext';
import { useLanguage } from '../../../../core/i18n/LanguageContext';
import { getGlassStyles } from './ProductManagementStyles';

const STOCK_STATUS_OPTIONS = [
  { value: 'green', label: 'green' },
  { value: 'yellow', label: 'yellow' },
  { value: 'red', label: 'red' },
];

// SOBKZ wird automatisch aus stock_type abgeleitet
const SOBKZ_FOR_TYPE = {
  own: '',
  vendor_consignment: 'K',
  at_vendor: 'O',
};

// Multi-Lagerort-Editor: verwaltet ein stock[]-Array mit
// { plant, storage_location, stock_type, vendor, special_stock_ind, qty, status }.
// Sendet Aenderungen via onStockChange(newArray).
const StockLocationsEditor = ({ stock, onStockChange, disabled = false }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const styles = getGlassStyles(isDarkMode);

  const addRow = () => {
    onStockChange([
      ...stock,
      { plant: '', storage_location: '', stock_type: 'own', vendor: '', special_stock_ind: '', qty: 0, status: 'green' },
    ]);
  };

  const removeRow = (idx) => {
    onStockChange(stock.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, field, value) => {
    const updated = stock.map((s, i) => {
      if (i !== idx) return s;
      const next = { ...s, [field]: field === 'qty' ? parseFloat(value) || 0 : value };
      // Bei Typ-Änderung: special_stock_ind automatisch ableiten + vendor leeren wenn own
      if (field === 'stock_type') {
        next.special_stock_ind = SOBKZ_FOR_TYPE[value] ?? '';
        if (value === 'own') next.vendor = '';
      }
      return next;
    });
    onStockChange(updated);
  };

  const inputSmall = `${styles.inputClassName} py-2 text-sm`;

  return (
    <div className="space-y-3">
      {Array.isArray(stock) && stock.length > 0 ? (
        <>
          {/* Tabellen-Kopf */}
          <div className={`grid grid-cols-12 gap-2 px-1 text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
            <span className="col-span-2">{t('admin.productForm.stock.plant')}</span>
            <span className="col-span-2">{t('admin.productForm.stock.storageLocation')}</span>
            <span className="col-span-2">{t('admin.productForm.stock.type')}</span>
            <span className="col-span-2">{t('admin.productForm.stock.qty')}</span>
            <span className="col-span-2">{t('admin.productForm.stock.status')}</span>
            <span className="col-span-1"></span>
          </div>

          {stock.map((row, idx) => {
            const isOwnType = !row.stock_type || row.stock_type === 'own';
            return (
              <div key={idx} className="space-y-1">
                {/* Hauptzeile */}
                <div className="grid grid-cols-12 gap-2 items-center">
                  {/* Werk */}
                  <div className="col-span-2 relative group/input">
                    <div className={styles.inputGlassBackground}></div>
                    <input
                      type="text"
                      value={row.plant}
                      onChange={(e) => updateRow(idx, 'plant', e.target.value)}
                      disabled={disabled}
                      placeholder="1000"
                      className={`${inputSmall} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <div className={styles.focusHighlight}></div>
                  </div>

                  {/* Lagerort (nur bei own sinnvoll, immer anzeigbar) */}
                  <div className="col-span-2 relative group/input">
                    <div className={styles.inputGlassBackground}></div>
                    <input
                      type="text"
                      value={row.storage_location}
                      onChange={(e) => updateRow(idx, 'storage_location', e.target.value)}
                      disabled={disabled || !isOwnType}
                      placeholder="0001"
                      className={`${inputSmall} ${(disabled || !isOwnType) ? 'opacity-40 cursor-not-allowed' : ''}`}
                    />
                    <div className={styles.focusHighlight}></div>
                  </div>

                  {/* Bestandsart */}
                  <div className="col-span-2 relative group/select">
                    <div className={styles.inputGlassBackground}></div>
                    <select
                      value={row.stock_type || 'own'}
                      onChange={(e) => updateRow(idx, 'stock_type', e.target.value)}
                      disabled={disabled}
                      className={`${inputSmall} appearance-none cursor-pointer ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      <option value="own" className={isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}>
                        {t('admin.productForm.stock.typeOwn')}
                      </option>
                      <option value="vendor_consignment" className={isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}>
                        {t('admin.productForm.stock.typeVendorConsignment')}
                      </option>
                      <option value="at_vendor" className={isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}>
                        {t('admin.productForm.stock.typeAtVendor')}
                      </option>
                    </select>
                    <div className={styles.focusHighlight}></div>
                  </div>

                  {/* Menge */}
                  <div className="col-span-2 relative group/input">
                    <div className={styles.inputGlassBackground}></div>
                    <input
                      type="number"
                      value={row.qty}
                      onChange={(e) => updateRow(idx, 'qty', e.target.value)}
                      disabled={disabled}
                      min="0"
                      step="1"
                      className={`${inputSmall} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <div className={styles.focusHighlight}></div>
                  </div>

                  {/* Status */}
                  <div className="col-span-2 relative group/select">
                    <div className={styles.inputGlassBackground}></div>
                    <select
                      value={row.status}
                      onChange={(e) => updateRow(idx, 'status', e.target.value)}
                      disabled={disabled}
                      className={`${inputSmall} appearance-none cursor-pointer ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      {STOCK_STATUS_OPTIONS.map((o) => (
                        <option
                          key={o.value}
                          value={o.value}
                          className={isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}
                        >
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <div className={styles.focusHighlight}></div>
                  </div>

                  {/* Zeile loeschen */}
                  <div className="col-span-1 flex justify-center">
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        title={t('admin.productForm.stock.removeRow')}
                        className={`p-1.5 rounded-lg transition-colors ${
                          isDarkMode
                            ? 'text-red-400/70 hover:text-red-400 hover:bg-red-500/10'
                            : 'text-red-500/70 hover:text-red-600 hover:bg-red-50'
                        }`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Lieferant-Input (nur bei Sonderbestand) */}
                {!isOwnType && (
                  <div className="grid grid-cols-12 gap-2 items-center pl-4">
                    <span className={`col-span-4 text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                      {t('admin.productForm.stock.vendor')}
                    </span>
                    <div className="col-span-7 relative group/input">
                      <div className={styles.inputGlassBackground}></div>
                      <input
                        type="text"
                        value={row.vendor || ''}
                        onChange={(e) => updateRow(idx, 'vendor', e.target.value)}
                        disabled={disabled}
                        placeholder="0007074189"
                        className={`${inputSmall} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                      />
                      <div className={styles.focusHighlight}></div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      ) : (
        <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
          {t('admin.productForm.stock.empty')}
        </p>
      )}

      {/* Zeile hinzufuegen */}
      {!disabled && (
        <button
          type="button"
          onClick={addRow}
          className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-xl border transition-colors ${
            isDarkMode
              ? 'border-white/20 text-white/70 hover:text-white hover:border-white/40 hover:bg-white/5'
              : 'border-slate-200 text-slate-600 hover:text-brand hover:border-brand/40 hover:bg-brand/5'
          }`}
        >
          <Plus className="w-4 h-4" />
          {t('admin.productForm.stock.addRow')}
        </button>
      )}
    </div>
  );
};

export default StockLocationsEditor;
